import { mobileAppUri, dataLoaderUri } from "../API";
import apiClient from "../../services/apiClient";

// ObjectivesAPI.tsx
export interface ConversationObjective {
  sectionId: string;           // PK: which lesson or section
  objectiveId: string;         // SK: unique per-objective
  
  // New schema fields
  base_lang_code?: string;     // e.g. "IT"
  targ_lang_code?: string;     // e.g. "ES"
  lang_pair_word_id?: string;  // e.g. "#Targ_ES#Base_IT#vives"
  legacy_vocab_identifier?: string;
  legacy_word_id?: string;
  level?: string;              // e.g. "Main-Verbs"
  langPair?: string;           // e.g. "LANGPAIR#IT->ES"
  lvlSectionObj?: string;      // composite key
  sectionObjective?: string;   // composite key
  wordObjective?: string;      // composite key
  
  // Legacy fields (still supported)
  wordId?: string;             // optional: the word being practiced
  baseLanguage?: string;       // e.g. "en" (legacy)
  targetLanguage?: string;     // e.g. "es" (legacy)
  
  // Common fields
  domain: string;              // e.g.: general, construction, medical
  proficiencyLevel: string;    // A1…C2
  difficulty: string;          // 1=easy,2=medium,3=hard
  objectiveBaseText: string;   // in user's native language
  objectiveTargetText: string; // in target language
  createdAt: string;           // ISO timestamp
}

// Fetch conversation objectives for a specific section
export const fetchConversationObjectives = async (sectionName: string): Promise<ConversationObjective[]> => {
  try {
    const data = await apiClient.get(
      `${mobileAppUri}/api/v1/conversation-objectives`,
      { params: { section: sectionName } }
    );
    
    // Check if the response has an 'items' property (backend format)
    if (data && Array.isArray(data.items)) {
      return data.items;
    }
    
    // Fallback: if it's already an array, return it directly
    if (Array.isArray(data)) {
      return data;
    }
    
    // If neither format matches, return empty array
    console.warn('Unexpected response format from conversation objectives API:', data);
    return [];
  } catch (error) {
    console.error('Error fetching conversation objectives:', error);
    throw error;
  }
};

// Create a new conversation objective
export const createConversationObjective = async (objective: Omit<ConversationObjective, 'createdAt'>): Promise<{message: string}> => {
  try {
    const data = await apiClient.post(
      `${dataLoaderUri}/api/v1/conversation-objectives`,
      objective
    );
    return data;
  } catch (error) {
    console.error('Error creating conversation objective:', error);
    throw error;
  }
};

// Update an existing conversation objective
export const updateConversationObjective = async (sectionId: string, objectiveId: string, objective: Partial<ConversationObjective>): Promise<{message: string}> => {
  try {
    const data = await apiClient.put(
      `${dataLoaderUri}/api/v1/conversation-objectives/${sectionId}/${objectiveId}`,
      objective
    );
    return data;
  } catch (error) {
    console.error('Error updating conversation objective:', error);
    throw error;
  }
};

// Delete a conversation objective
export const deleteConversationObjective = async (sectionId: string, objectiveId: string): Promise<void> => {
  try {
    await apiClient.delete(
      `${dataLoaderUri}/api/v1/conversation-objectives/${sectionId}/${objectiveId}`
    );
  } catch (error) {
    console.error('Error deleting conversation objective:', error);
    throw error;
  }
};